import { useCallback, useEffect, useMemo, useState } from "react";
import { Plus } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { ExpenseFormDialog } from "../components/ExpenseFormDialog";
import { ProfitFormDialog } from "../components/ProfitFormDialog";
import { ReportFilters } from "../components/ReportFilters";
import { ReportLedgerTable } from "../components/ReportLedgerTable";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";

const today = new Date();

const blankExpense = () => ({
  description: "",
  amount: "",
  category: "other",
  expense_date: today.toISOString().slice(0, 10),
  notes: "",
  agent_id: "",
});

const blankProfit = () => ({
  description: "",
  amount: "",
  profit_date: today.toISOString().slice(0, 10),
  notes: "",
  agent_id: "",
});

export default function ReportsLedgerPage() {
  const [user, setUser] = useState(null);
  const [scopes, setScopes] = useState([]);
  const [year, setYear] = useState(String(today.getFullYear()));
  const [month, setMonth] = useState(String(today.getMonth() + 1));
  const [ownerId, setOwnerId] = useState("");
  const [includeVoid, setIncludeVoid] = useState(false);
  const [report, setReport] = useState(null);
  const [expenseOpen, setExpenseOpen] = useState(false);
  const [expense, setExpense] = useState(blankExpense);
  const [editingExpense, setEditingExpense] = useState(null);
  const [deletingExpense, setDeletingExpense] = useState(null);
  const [profitOpen, setProfitOpen] = useState(false);
  const [profit, setProfit] = useState(blankProfit);
  const [editingProfit, setEditingProfit] = useState(null);
  const [deletingProfit, setDeletingProfit] = useState(null);

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    if (year) {
      params.set("year", year);
    }
    if (month) {
      params.set("month", month);
    }
    if (ownerId) {
      params.set("owner_id", ownerId);
    }
    if (includeVoid) {
      params.set("include_void", "true");
    }
    const [reportResponse, scopeResponse, userResponse] = await Promise.all([
      api.get(`/reports/detailed?${params}`),
      api.get("/sales-scopes"),
      api.get("/auth/me"),
    ]);
    setReport(reportResponse.data);
    setScopes(scopeResponse.data);
    setUser(userResponse.data);
  }, [includeVoid, month, ownerId, year]);

  useEffect(() => {
    load();
  }, [load]);

  const availableScopes = useMemo(() => {
    if (!user || user.role !== "officer") {
      return scopes;
    }
    return scopes.filter((scope) => !scope.is_self);
  }, [scopes, user]);

  const updateFilter = useCallback((field, value) => {
    if (field === "year") {
      setYear(value);
    }
    if (field === "month") {
      setMonth(value);
    }
    if (field === "ownerId") {
      setOwnerId(value);
    }
    if (field === "includeVoid") {
      setIncludeVoid(value);
    }
  }, []);

  const updateExpense = useCallback((field, value) => {
    setExpense((current) => ({ ...current, [field]: value }));
  }, []);

  const updateProfit = useCallback((field, value) => {
    setProfit((current) => ({ ...current, [field]: value }));
  }, []);

  const saveExpense = async (event) => {
    event.preventDefault();
    try {
      const payload = { ...expense, amount: Number(expense.amount) };
      if (editingExpense) {
        await api.put(`/expenses/${editingExpense.expense_id}`, payload);
        toast.success("Expense updated");
      } else {
        await api.post("/expenses", payload);
        toast.success("Expense added");
      }
      setExpenseOpen(false);
      setExpense(blankExpense());
      setEditingExpense(null);
      load();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const openAddExpense = () => {
    setExpense(blankExpense());
    setEditingExpense(null);
    setExpenseOpen(true);
  };

  const openEditExpense = (transaction) => {
    setExpense({
      description: transaction.description,
      amount: String(transaction.expense),
      category: transaction.expense_category || "other",
      expense_date: transaction.date,
      notes: transaction.expense_notes || "",
      agent_id: transaction.expense_owner_id || "",
    });
    setEditingExpense(transaction);
    setExpenseOpen(true);
  };

  const deleteExpense = async () => {
    try {
      await api.delete(`/expenses/${deletingExpense.expense_id}`);
      toast.success("Expense deleted");
      setDeletingExpense(null);
      load();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const saveProfit = async (event) => {
    event.preventDefault();
    try {
      const payload = { ...profit, amount: Number(profit.amount) };
      if (editingProfit) {
        await api.put(`/profits/${editingProfit.profit_id}`, payload);
        toast.success("Profit updated");
      } else {
        await api.post("/profits", payload);
        toast.success("Profit added");
      }
      setProfitOpen(false);
      setProfit(blankProfit());
      setEditingProfit(null);
      load();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const openAddProfit = () => {
    setProfit(blankProfit());
    setEditingProfit(null);
    setProfitOpen(true);
  };

  const openEditProfit = (transaction) => {
    setProfit({
      description: transaction.description,
      amount: String(transaction.credit),
      profit_date: transaction.date,
      notes: transaction.profit_notes || "",
      agent_id: transaction.profit_owner_id || "",
    });
    setEditingProfit(transaction);
    setProfitOpen(true);
  };

  const deleteProfit = async () => {
    try {
      await api.delete(`/profits/${deletingProfit.profit_id}`);
      toast.success("Profit deleted");
      setDeletingProfit(null);
      load();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const reportFilters = useMemo(() => ({
    year,
    month,
    ownerId,
    includeVoid,
  }), [includeVoid, month, ownerId, year]);

  if (!report || !user) {
    return <div className="page-loading" data-testid="reports-loading">Preparing report…</div>;
  }

  const canAddExpense = ["admin", "master"].includes(user.role);
  return (
    <div className="page reports-ledger-page" data-testid="reports-page">
      <header className="page-header">
        <div>
          <h1>Reports</h1>
          <p>Generate monthly transaction reports</p>
        </div>
        {canAddExpense && (
          <div className="report-header-actions">
            <Button onClick={openAddExpense} data-testid="add-expense-button">
              <Plus size={16} /> Add Expense
            </Button>
            <Button onClick={openAddProfit} data-testid="add-profit-button">
              <Plus size={16} /> Add Profit
            </Button>
          </div>
        )}
      </header>
      <ReportFilters
        filters={reportFilters}
        onChange={updateFilter}
        onSearch={load}
        scopes={availableScopes}
        user={user}
      />
      <ReportLedgerTable
        canManageExpenses={canAddExpense}
        onDeleteExpense={setDeletingExpense}
        onDeleteProfit={setDeletingProfit}
        onEditExpense={openEditExpense}
        onEditProfit={openEditProfit}
        report={report}
      />
      <ExpenseFormDialog
        expense={expense}
        isEditing={Boolean(editingExpense)}
        onChange={updateExpense}
        onOpenChange={setExpenseOpen}
        onSubmit={saveExpense}
        open={expenseOpen}
        users={scopes}
      />
      <ProfitFormDialog
        profit={profit}
        isEditing={Boolean(editingProfit)}
        onChange={updateProfit}
        onOpenChange={setProfitOpen}
        onSubmit={saveProfit}
        open={profitOpen}
        users={scopes}
      />
      <Dialog
        open={Boolean(deletingExpense)}
        onOpenChange={(open) => !open && setDeletingExpense(null)}
      >
        <DialogContent data-testid="delete-expense-dialog">
          <DialogHeader>
            <DialogTitle>Delete expense?</DialogTitle>
            <DialogDescription>
              This action removes the selected expense from the report and retains an audit record.
            </DialogDescription>
          </DialogHeader>
          <p data-testid="delete-expense-confirmation">
            This removes “{deletingExpense?.description}” from the report. The deletion remains
            in the audit history.
          </p>
          <div className="dialog-actions">
            <Button
              variant="outline"
              onClick={() => setDeletingExpense(null)}
              data-testid="cancel-delete-expense-button"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={deleteExpense}
              data-testid="confirm-delete-expense-button"
            >
              Delete Expense
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog
        open={Boolean(deletingProfit)}
        onOpenChange={(open) => !open && setDeletingProfit(null)}
      >
        <DialogContent data-testid="delete-profit-dialog">
          <DialogHeader>
            <DialogTitle>Delete profit?</DialogTitle>
            <DialogDescription>
              This action removes the selected profit from report credit and Profit or Loss.
            </DialogDescription>
          </DialogHeader>
          <p data-testid="delete-profit-confirmation">
            This removes “{deletingProfit?.description}” from the report. The deletion remains
            in the audit history.
          </p>
          <div className="dialog-actions">
            <Button
              variant="outline"
              onClick={() => setDeletingProfit(null)}
              data-testid="cancel-delete-profit-button"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={deleteProfit}
              data-testid="confirm-delete-profit-button"
            >
              Delete Profit
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}